import { useRef, useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import ClientContext from 'contexts/ClientContext'
import Button from 'components/common/Button/Button'
import PageSubtitle from 'components/common/PageSubtitle/PageSubtitle'
import PageTitle from 'components/common/PageTitle/PageTitle'
import Card from 'components/common/Card/Card'
import DocumentText from 'components/assets/icons/DocumentText'
import style from './DocumentForm.module.scss'
import api from 'helpers/api'
import Close from 'components/assets/icons/Close'
import Collapsible from 'components/common/Collapsible/Collapsible'
import CardLine from 'components/common/CardLine/CardLine'
import dayjs from 'dayjs'
import Thundercloud from 'components/assets/icons/Thundercloud'
import Download from 'components/assets/icons/Download';
import InfoMessage from 'components/common/InfoMessage/InfoMessage'
import ExclamationTriangle from 'components/assets/icons/ExclamationTriangle'
import Status from 'components/common/Status/Status'
import { ADHESION_STATUS_TYPE, isAdhesionInactive } from 'constants/adhesionStatus'

const DocumentForm = () => {
  const { t, i18n: { language } } = useTranslation()
  const navigate = useNavigate()
  const { state } = useLocation()
  const inputRef = useRef([])
  const [ fileList, setFileList ] = useState([])
  const [ isHover, setIsHover ] = useState(false)
  const isVideoSelected = fileList.find( doc => doc?.type.startsWith('video/') )
  const [ error, setError ] = useState(null)
  const [ contract, setContract ] = useState(state?.contract)
  const { claim, claimNature } = state || {}
  const [ natureName, setNatureName ] = useState(claimNature.label)
  const [ proofOfClaims, setProofOfClaims ] = useState(claimNature.proofOfClaims)
  const { color, colabelNeutralDark } = useContext(ClientContext)
  const fetched = useRef(null)

  const displayError = (msg) => {
    window.scrollTo(0, 0)
    setError(msg)
    setTimeout(() => setError(null), 5000)
  }

  useEffect(() => {
    const getContract = async () => {
      fetched.current = language
      try {
        const res = await api.get({ url: `/adhesions/${contract._id}/client` })
        const nature = res?.adhesion.rateType.claimNatures.find( data => data._id === claim.nature.value)
        setNatureName(nature?.name)
        setProofOfClaims(nature?.proofOfClaims || [])
        if (res?.adhesion) setContract(res.adhesion)
      } catch (error) {
        console.log('Fetch contracts error')
      }
    }
    if (fetched.current !== language) getContract()
  }, [language, contract, fetched])

  const submit = async (e) => {
    e.preventDefault()
    try {
      const formData = new FormData()
      fileList.forEach( file => {
        if (file){
          const mixedName = JSON.stringify({
            name: file.name,
            proofOfClaimsId: file.proofOfClaimsId
          })
          formData.append('files', file, mixedName)
        }
      })

      const finalData = {
        ...claim,
        adhesionId: contract._id,
        nature: claim.nature.value
      }

      for (let key in finalData) {
        formData.append(key, finalData[key])
      }

      const res = await api.post({
        url: '/claims',
        contentType: 'multipart/form-data',
        data: formData,
      })

      navigate(`/claims/${res.response.id}` )
    } catch (error) {
      console.log('Create claim error')
      displayError(t('common:form.genericError'))
    }
  }

  const handleFileChange = (event, index) => {
    if (!event.target.files) {
      return
    }

    const item = event.target.files[0]

    if (item.size > process.env.REACT_APP_FILESIZE_LIMIT){
      displayError(t('claim:item.fileTooLarge'))
    }  else if (item.type.startsWith('video/') && isVideoSelected){
      displayError(t('claim:item.limitVideoQuantity'))
    }  else if (!item.type.match(/image|pdf|video/i)) {
      displayError(t('claim:item.fileFormatForbidden'))
    } else {
      const newFileList = [...fileList]
      item.proofOfClaimsId = claim.nature.proofOfClaims[index]._id
      newFileList[index] = item
      setFileList(newFileList)
    }

  }

  const handleUploadClick = idx => {
    if (inputRef.current[idx]) {
      inputRef.current[idx].click()
    }
  }

  const removeFile = index => {
    const newFileList = [...fileList]
    newFileList.splice(index, 1, null)
    setFileList(newFileList)
  }

  const resizeFileName = fileName => {
    const limit = process.env.REACT_APP_FILENAME_LIMIT
    const [name, ext] = fileName.split(/\.(?=[a-z0-9]+$)/i)
    const shorterName = name.length > limit ? name.slice(0, limit)+'(...)' : name
    return `${shorterName}.${ext}`
  }


  return (
    <>
      <PageTitle text={t('claim:applySteps.documents.title')} />
      <PageSubtitle text={t('claim:applySteps.documents.subtitle')} />

      {error &&
        <InfoMessage
          className={style.infoMessage}
          type='error'
          label={error}
          prefix={<ExclamationTriangle />}
        />
      }

      <Card
        className={style.contract}
        inactive={contract.cancelation || isAdhesionInactive(contract.adhesionStatus)}
        content={
          <div className={style['contract-content']}>
            <div>
              {contract.rateType.dashboardPackageDescription || contract.contractType.dashboardDescription}
              {contract.contractType.displayCardNumber &&
                <ul className={style.cards}>
                  {contract.virtualCard && <li>{t('common:virtualCard')} : {`**** **** **** ${contract.virtualCard.slice(-4)}`}</li>}
                  {contract.physicalCard && <li>{t('common:physicalCard')} : {`**** **** **** ${contract.physicalCard.slice(-4)}`}</li>}
                  {contract.physicalCard2 && <li>{t('common:physicalCard2')} : {`**** **** **** ${contract.physicalCard2.slice(-4)}`}</li>}
                </ul>
              }
            </div>
            {contract.adhesionStatus && (
              <Status
                label={t(`common:adhesionStatus.${contract.adhesionStatus}`, { defaultValue: contract.adhesionStatus })}
                type={ADHESION_STATUS_TYPE[contract.adhesionStatus] || 'adhesionSigned'}
              />
            )}
          </div>
        }
        title={
          contract.rateType?.dashboardPackageName
            ? <>{contract.rateType.dashboardPackageName} - <i>{contract.idTransaction}</i></>
            : <>{contract.contractType.dashboardName} - <i>{contract.idTransaction}</i></>
        }
        titleIcon={<DocumentText styles={color && {fill: colabelNeutralDark}} />}
      />

      <Collapsible
        title={
          <>
            <Thundercloud styles={{ fill: color ? colabelNeutralDark : '#1414FF' }} />
            <div className={style['collapsible-title']}>{t('claim:claim')}</div>
          </>
        }
        isOpenedProp={true}
        content={
          <>
            <div className={style['summary-first-line']}>
              <CardLine
                className={style['first-line-element']}
                title={t('claim:item.date')}
                content={dayjs(claim.createdAt).format('DD/MM/YYYY')}
              />
              <CardLine
                className={style['first-line-element']}
                title={t('claim:item.location')}
                content={claim.location}
              />
              <CardLine
                className={style['first-line-element']}
                title={t('claim:item.nature')}
                content={natureName}
              />
            </div>
            <CardLine
              title={t('claim:item.circumstances')}
              content={claim.circumstance}
            />
            {!!(proofOfClaims.length > 0) &&
              <CardLine
                className={style['required-documents']}
                title={<>
                  {t('claim:applySteps.documents.requiredDocuments')}
                  <br/>
                  {t('claim:applySteps.documents.contact', {
                      mail: claimNature.classifiedClaim
                        ? 'medecin@getowen.eu'
                        : 'claim@getowen.eu'
                  })}
                </>}
                styles={color && {color: colabelNeutralDark}}
                content={
                  <ul>
                    {proofOfClaims.map( (doc, index) => {
                      const file = fileList[index]
                      return (
                        <li key={`document-${index}`}>
                          <legend>
                            {doc.name}
                          </legend>
                          {!file
                            ? <div
                              onClick={() => handleUploadClick(index)}
                              onMouseEnter={() => setIsHover(index)}
                              onMouseLeave={() => setIsHover(null)}
                              style={{
                                cursor:'pointer',
                                color: color && (isHover === index ? color : colabelNeutralDark)
                              }}
                            >
                              <Download styles={color && {fill: (isHover === index ? color : colabelNeutralDark)}} />
                              <span styles={color && {color: (isHover === index ? color : colabelNeutralDark)}}>{t('common:upload')}</span>
                              <input
                                type="file"
                                accept={'image/*, .pdf' + (!isVideoSelected ? ', video/*' : '')}
                                ref={el => (inputRef.current[index] = el)}
                                onChange={e => handleFileChange(e, index)}
                                hidden
                              />
                            </div>
                            : <div onClick={() => removeFile(index)}>
                              <Close />
                              <span>{resizeFileName(file.name)}</span>
                            </div>
                          }
                        </li>
                      )
                    })}
                  </ul>
                }
              />
            }
          </>
        }
      />

      <form className={style.form}>
        <Button
          className={style.button}
          label={t('common:submit')}
          onClick={(e)=>submit(e)}
          type="primary"
        />
      </form>
    </>
  )
}

export default DocumentForm
